import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Select } from "../ui/input";
import { formatDateTime } from "../../lib/format";
import {
  CRM_PRIORITY_LABELS,
  CRM_TASK_STATUS_LABELS,
  CRM_TASK_TYPE_LABELS,
  dateTimeLocalToIso,
  formatRelativeDay,
  isOverdue,
  toDateTimeLocalValue,
} from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmPriority, CrmTask, CrmTaskStatus, CrmTaskType } from "@shared/types";

interface Draft {
  title: string;
  type: CrmTaskType;
  priority: CrmPriority;
  dueAt: string;
  assignedUserId: string;
  notes: string;
}

const EMPTY: Draft = {
  title: "",
  type: "relance",
  priority: "normal",
  dueAt: "",
  assignedUserId: "",
  notes: "",
};

interface TaskListProps {
  accountId: string;
  items: CrmTask[];
  users: Array<{ id: string; name: string }>;
  onChanged: () => void;
}

export function TaskList({ accountId, items, users, onChanged }: TaskListProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const base = `/api/crm/accounts/${accountId}/tasks`;

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim() || saving) return;
    setSaving(true);
    try {
      await api(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify({
          title: draft.title.trim(),
          type: draft.type,
          priority: draft.priority,
          // Une échéance saisie sans heure est ancrée à midi : minuit local
          // bascule la veille dans les fuseaux à l'ouest de UTC.
          dueAt: dateTimeLocalToIso(draft.dueAt ? `${draft.dueAt}T12:00` : ""),
          assignedUserId: draft.assignedUserId || null,
          notes: draft.notes.trim() || null,
        }),
      });
      push("success", editingId ? "Suivi mis à jour" : "Suivi ajouté");
      reset();
      onChanged();
    } catch {
      push("danger", "Échec de l'enregistrement du suivi");
    } finally {
      setSaving(false);
    }
  };

  const edit = (t: CrmTask) => {
    setEditingId(t.id);
    setDraft({
      title: t.title,
      type: t.type,
      priority: t.priority,
      dueAt: toDateTimeLocalValue(t.dueAt).slice(0, 10),
      assignedUserId: t.assignedUserId ?? "",
      notes: t.notes ?? "",
    });
  };

  const changeStatus = async (id: string, status: CrmTaskStatus) => {
    try {
      await api(`${base}/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      onChanged();
    } catch {
      push("danger", "Échec du changement de statut");
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
      if (editingId === id) reset();
      push("success", "Suivi supprimé");
      onChanged();
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  const openCount = items.filter((t) => t.status === "open").length;

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">Suivis & prochaines actions</h2>
        <div className="crm-section__actions">
          <span className="crm-chip crm-chip--muted">{openCount} à faire</span>
        </div>
      </div>

      <form className="crm-form crm-form--inline" onSubmit={submit}>
        <Input
          placeholder="Prochaine action (ex : rappeler pour la démo)"
          aria-label="Titre du suivi"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <Select
          aria-label="Type de suivi"
          value={draft.type}
          onChange={(e) => setDraft({ ...draft, type: e.target.value as CrmTaskType })}
        >
          {Object.entries(CRM_TASK_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Priorité"
          value={draft.priority}
          onChange={(e) => setDraft({ ...draft, priority: e.target.value as CrmPriority })}
        >
          {Object.entries(CRM_PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              Priorité {label.toLowerCase()}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          aria-label="Échéance"
          title="Échéance"
          value={draft.dueAt}
          onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })}
        />
        <Select
          aria-label="Responsable du suivi"
          value={draft.assignedUserId}
          onChange={(e) => setDraft({ ...draft, assignedUserId: e.target.value })}
        >
          <option value="">Non assigné</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        {editingId ? (
          <Input
            placeholder="Notes"
            aria-label="Notes du suivi"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        ) : null}
        <div className="crm-form__actions">
          {editingId ? (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving || !draft.title.trim()}>
            {editingId ? "Enregistrer" : "Ajouter"}
          </Button>
        </div>
      </form>

      <table className="table crm-table">
        <thead>
          <tr>
            <th>Suivi</th>
            <th>Échéance</th>
            <th>Responsable</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={5} className="table__empty">
                Aucun suivi — planifie la prochaine action ci-dessus.
              </td>
            </tr>
          ) : (
            items.map((t) => {
              const late = t.status === "open" && isOverdue(t.dueAt);
              return (
                <tr key={t.id}>
                  <td data-label="Suivi" title={t.notes ?? undefined}>
                    {t.title}{" "}
                    <span className="crm-chip crm-chip--muted">{CRM_TASK_TYPE_LABELS[t.type]}</span>{" "}
                    {t.priority === "high" ? <span className="crm-chip">Haute</span> : null}
                  </td>
                  <td data-label="Échéance" title={formatDateTime(t.dueAt)}>
                    {t.dueAt ? (
                      <Badge variant={late ? "danger" : "neutral"}>
                        {formatRelativeDay(t.dueAt)}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td data-label="Responsable">{users.find((u) => u.id === t.assignedUserId)?.name ?? "—"}</td>
                  <td data-label="Statut">
                    <Select
                      aria-label={`Statut du suivi ${t.title}`}
                      value={t.status}
                      onChange={(e) => void changeStatus(t.id, e.target.value as CrmTaskStatus)}
                    >
                      {Object.entries(CRM_TASK_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="table__actions">
                    <Button variant="ghost" size="sm" onClick={() => edit(t)}>
                      Modifier
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void remove(t.id)}>
                      Supprimer
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </section>
  );
}
